"use client";

import React, { useCallback, useState } from "react";
import { Dialog, DialogContent, DialogTrigger } from "./ui/dialog";
import { Flex, Text } from "@radix-ui/themes";
import { Button } from "./ui/button";
import { BidInfo } from "../auctions/[auctionId]/lots/[lotId]/BiddingCard";
import { formatCurrency } from "@/lib/utils";
import { BidType } from "@prisma/client";
import { Separator } from "./ui/separator";
import PaymentMethodSelect, { SavePaymentSelectionHandle } from "./creditCardSelect/PaymentMethodSelect";
import { Loader2 } from "lucide-react";
import TermsAcceptancePopup from "../auctions/[auctionId]/lots/[lotId]/TermsAcceptancePopup";
import { useSession } from "next-auth/react";
import { useTermsAcceptance } from "@/lib/hooks/useTermsAcceptance";
import { warn } from "console";
import classNames from "classnames";

interface Props {
  isOpen: boolean | undefined;
  setIsOpen: (val: boolean) => void;
  onDismiss: () => void;
  onConfirm: () => Promise<void>;
  bidInfo: BidInfo;
  auctionId: number;
  flash?: boolean;
}

const getBidMessage = (bidType: BidType) => {
  switch (bidType) {
    case "STANDARD":
      return "You are about to place a bid for";
    case "ABSENTEE":
      return "";
    case "MAX":
      return "You are about to place a max bid for ";
  }
};

const getConfirmBidMessage = (bidType: BidType) => {
  switch (bidType) {
    case "STANDARD":
      return "Confirm bid";
    case "ABSENTEE":
      return "";
    case "MAX":
      return "Confirm max bid";
  }
};

const BidPopup = React.forwardRef<SavePaymentSelectionHandle, Props>(
  (props, ref) => {
    const { isOpen, setIsOpen, onConfirm, onDismiss, bidInfo, auctionId, flash } = props;
    const [disableConfirm, setDisableConfirm] = useState(true);
    const [loading, setLoading] = useState(false);
    const session: any = useSession();

    const handleClick = async () => {
      setLoading(true);
      await onConfirm();
      setIsOpen(false);
      setLoading(false);
    };

    const initializeCreditCardSelect = useCallback(() => {
      setDisableConfirm(true);
    }, []);

    const onValidCard = useCallback(() => setDisableConfirm(false), []);
    const onNoValidCard = useCallback(() => setDisableConfirm(true), []);

    // TODO: Make this automatically use the id of the current auction.
    const { isAccepted, accept } = useTermsAcceptance(session?.data?.userId, auctionId);

    // TODO: Spiel about max bids, or link to another page?

    // TODO: Need to save user CC preference. Key based on
    // on auctionId and userId.
    return (
      <>
        <Dialog
          open={isOpen}
          onOpenChange={(open) => {
            setIsOpen(open);
            if (!open) {
              onDismiss();
            }
          }}
        >
          <DialogTrigger onClick={() => setIsOpen(true)} />
          <DialogContent className="h-auto items-center gap-0">
            <Flex
              direction="column"
              justify="start"
              align="center"
              className="space-y-4 pt-3"
            >
              <Flex direction="column" align="center" gap="1">
                <Text className="text-lg font-medium select-none">
                  {getBidMessage(bidInfo.bidType)}
                </Text>
                <Flex
                  justify="center"
                  align="center"
                  className={classNames(
                    flash && "bg-gray-200",
                    "transition-colors rounded-md w-fit px-1"
                  )}
                >
                  <Text className="text-3xl font-semibold text-indigo-600 select-none">
                    {formatCurrency(bidInfo.amount)}
                  </Text>
                </Flex>
                {bidInfo.bidType === "MAX" && (
                  <Text className="text-xs text-foreground">
                    By confirming, you authorize us to bid on your behalf up to the above.
                  </Text>
                )}
              </Flex>
              <Separator />
              <Flex align="start" className="w-full pl-3">
                <Text className="text-xs text-muted-foreground">
                  Please select your preferred fallback payment method.
                  You can change it on subsequent bids.
                </Text>
              </Flex>
              <PaymentMethodSelect
                ref={ref}
                onValidCardSelected={onValidCard}
                onInvalidCardSelected={onNoValidCard}
                initialize={initializeCreditCardSelect}
                auctionId={auctionId}
              />
              <Button
                className="w-full h-12"
                onClick={handleClick}
                disabled={loading || disableConfirm}
              >
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {getConfirmBidMessage(bidInfo.bidType)}
              </Button>
            </Flex>
          </DialogContent>
        </Dialog>
        <TermsAcceptancePopup
          auctionId={auctionId}
          isOpen={!isAccepted && !!isOpen}
          onAccept={accept}
          onOpenChange={setIsOpen}
          onViewTerms={() => setIsOpen(false)}
        />
      </>
    );
  }
);

BidPopup.displayName = "BidPopup";

export default BidPopup;
